import { useState, useMemo, useCallback, useEffect } from 'react';
import type { VariableNode } from 'services/entities/VariablesEntity';

// The caller always already holds the exact node it's navigating to — ascending via a breadcrumb
// (a real chain entry), or descending via an item taken straight from the currently displayed
// list. Matching by reference instead of re-deriving a lookup from `path` sidesteps two problems:
// CRM data can stamp the same `path` string on unrelated nodes in different tree branches, and a
// displayed item isn't always a direct child in the raw tree (e.g. deriveArrayFieldSchema hoists
// nested-array entries from several levels deep into the list VariableFolders renders).
// `chain` is the real root-to-current ancestor path (root inclusive); returns a new root-exclusive
// navigation stack: truncated back to `target` if it's already an ancestor, otherwise `target`
// pushed as the new leaf.
function resolveTarget(chain: VariableNode[], target: VariableNode): VariableNode[] {
  const ancestorIndex = chain.findIndex((node) => node === target);
  if (ancestorIndex !== -1) return chain.slice(1, ancestorIndex + 1);

  return [...chain.slice(1), target];
}

export function useVariableNavigation(data: VariableNode | null) {
  // Root-exclusive stack of the real nodes navigated through; activeNode is the last entry, or
  // `data` itself when empty. Real node references, not re-derived path strings.
  const [navigationStack, setNavigationStack] = useState<VariableNode[]>([]);
  const [initialLoad, setInitialLoad] = useState(true);

  // A fresh `data` tree (e.g. after a CRM link/unlink refetch) invalidates any node references
  // held from the previous tree — they're not valid positions in the new one.
  useEffect(() => {
    setNavigationStack([]);
  }, [data]);

  const activeNode = useMemo(() => {
    if (!data) return null;
    return navigationStack.length > 0 ? navigationStack[navigationStack.length - 1] : data;
  }, [data, navigationStack]);

  const breadcrumbs = useMemo(() => {
    if (initialLoad || !data || navigationStack.length === 0) return [];
    return [data, ...navigationStack];
  }, [initialLoad, data, navigationStack]);

  const selectNode = useCallback(
    (node: VariableNode) => {
      setInitialLoad(false);
      if (!data) return;
      setNavigationStack((stack) => resolveTarget([data, ...stack], node));
    },
    [data]
  );

  const resetPath = useCallback(() => setNavigationStack([]), []);

  return { activeNode, breadcrumbs, currentPath: activeNode?.path ?? '', selectNode, resetPath, initialLoad };
}
